import React, { useState, useEffect, useRef } from 'react';

/**
 * Unified SpeciesCard component with multiple variants
 * Supports detailed, grid, and compact display modes
 * 
 * @param {string} variant - Display variant: 'detailed' | 'grid' | 'compact'
 * @param {string} name - Species name
 * @param {string} image - Image URL or path
 * @param {string} description - Species description text
 * @param {string} habitat - Habitat information (optional)
 * @param {Array<string>} areas - List of area names where species can be found
 * @param {string} soundPath - Optional path to audio file
 * @param {boolean} expandable - Whether the card content is collapsible
 * @param {number} maxAreasShown - Maximum number of area tags to show before "+N"
 * @param {Function} onImageError - Callback for image loading errors
 * @param {string} categoryName - Category name for the species
 * @param {boolean} loadingImage - Whether the image is still loading
 * @param {boolean} collected - Whether species is collected (grid variant)
 * @param {Object} species - Species object (for grid variant compatibility)
 */
export default function SpeciesCard({ 
    variant = 'detailed',
    speciesId,
    name,
    images = [],
    description,
    habitat,
    areas = [],
    soundPath,
    expandable = true,
    maxAreasShown = 2,
    onImageError,
    categoryName,
    loadingImage = false,
    collected = false,
    species, // For backward compatibility with grid variant
    fetchSpeciesImage, // Function to lazy load images
    ...props
}) {
    // Support species object prop (from userProfile variant)
    const displayName = name || species?.name;
    const displayImage = images || species?.image;
    const displayCategory = categoryName || species?.category;
    const isCollected = collected || species?.collected || false;

    

    // Render appropriate variant
    if (variant === 'grid') {
        return <GridVariant 
            name={displayName} 
            image={displayImage} 
            category={displayCategory}
            collected={isCollected}
            {...props}
        />;
    }
    
    if (variant === 'compact') {
        return <CompactVariant 
            name={displayName}
            image={displayImage}
            category={displayCategory}
            loadingImage={loadingImage}
            onImageError={onImageError}
            {...props}
        />;
    }

    // Default: detailed variant
    return <DetailedVariant
        speciesId={speciesId}
        name={displayName}
        images={displayImage}
        description={description}
        habitat={habitat}
        areas={areas}
        soundPath={soundPath}
        expandable={expandable}
        maxAreasShown={maxAreasShown}
        onImageError={onImageError}
        categoryName={displayCategory}
        loadingImage={loadingImage}
        fetchSpeciesImage={fetchSpeciesImage}
        {...props}
    />;
}

/**
 * Grid variant - Compact card for profile/collection grids
 */
function GridVariant({ name, image, category, collected }) {
    return (
        <div className={`group relative w-full aspect-square bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 ${collected ? '' : 'opacity-70'}`}>
            {/* Image Container - Full Cover */}
            <img 
                src={image} 
                alt={name}
                className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 ${!collected ? 'grayscale' : ''}`}
            />
            
            {/* Gradient Overlay for Text Readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80" />

            {/* Collected Badge */}
            {collected && (
                <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full shadow-sm z-10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                </div>
            )}

            {/* Content */}
            <div className="absolute bottom-0 left-0 right-0 p-2 text-white z-10">
                <h4 className="font-semibold text-xs sm:text-sm truncate drop-shadow-md">{name}</h4>
                <p className="text-[10px] text-gray-300 truncate drop-shadow-md">{category}</p>
            </div>
        </div>
    );
}

/**
 * Compact variant - Simple card without expansion
 */
function CompactVariant({ name, image, category, loadingImage, onImageError }) {
    return (
        <article className="bg-beige rounded-xl overflow-hidden shadow-lg">
            {loadingImage ? (
                <div className="h-40 w-full bg-sage/20 flex items-center justify-center">
                    <span className="text-charcoal/50">Laddar bild...</span>
                </div>
            ) : image ? (
                <img
                    alt={name}
                    src={image}
                    className="h-40 w-full object-cover"
                    onError={onImageError}
                />
            ) : (
                <div className="h-40 w-full bg-sage/20 flex items-center justify-center">
                    <span className="text-charcoal/50">Ingen bild tillgänglig</span>
                </div>
            )}
            <div className="p-3">
                <h3 className="font-heading text-base font-semibold text-charcoal">{name}</h3>
                {category && <p className="text-xs text-charcoal/60 mt-1">{category}</p>}
            </div>
        </article>
    );
}

/**
 * Detailed variant - Full featured card with expansion
 */
function DetailedVariant({
    speciesId,
    name,
    images = [],
    description,
    habitat,
    areas,
    soundPath,
    expandable,
    maxAreasShown,
    onImageError,
    loadingImage,
    fetchSpeciesImage
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const cardRef = useRef(null);
    const touchStartX = useRef(0);

    //For images carusel and dots

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const hasMultipleImages = Array.isArray(images) && images.length > 1;

    useEffect(() => {
        if (Array.isArray(images) && images.length > 0) {
            setCurrentImageIndex(0);
        }
    }, [images]);

    const goNext = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) =>
            prev === images.length - 1 ? 0 : prev + 1
        );
    };

    const goPrev = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) =>
            prev === 0 ? images.length - 1 : prev - 1
        );
    };

    

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;

        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                // swipe left → next
                goNext(e)
            } else {
                // swipe right → prev
                goPrev(e)
            }
        }
    };


    // Lazy load image when card comes into viewport
    useEffect(() => {
        if (!fetchSpeciesImage || !speciesId || images === 'loading' || (Array.isArray(images) && images.length > 0)) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        fetchSpeciesImage(speciesId);
                        observer.disconnect(); // Only load once
                    }
                });
            },
            {
                rootMargin: '250px', // Start loading 250px before card is shown!
                threshold: 0.01
            }
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (observer) observer.disconnect();
        };
    }, [speciesId, images, fetchSpeciesImage]);

    const toggleExpanded = () => {
        if (expandable) {
            setIsExpanded(!isExpanded);
        }
    };

    const displayedAreas = areas.slice(0, maxAreasShown);
    const remainingAreasCount = areas.length - maxAreasShown;

    const defaultImageErrorHandler = (e) => {
        e.target.onerror = null;
        e.target.src = '';
        e.target.style.display = 'none';
        const parent = e.target.parentElement;
        if (parent) {
            parent.innerHTML = '<div class="h-56 w-full bg-sage/20 flex items-center justify-center"><span class="text-charcoal/50">Ingen bild tillgänglig</span></div>';
        }

    
        console.log("IMAGES:", images);
        console.log("CURRENT INDEX:", currentImageIndex);
    };

    return (
        <article ref={cardRef} className="bg-beige rounded-xl overflow-hidden shadow-lg flex flex-col h-fit">
           

            {images === 'loading' || loadingImage ? (
                <div className="h-56 w-full bg-sage/20 flex items-center justify-center">
                    <span className="text-charcoal/50">Laddar bild...</span>
                </div>
            ) : Array.isArray(images) && images.length > 0 ? (

            
                
                <div className="relative h-56 w-full overflow-hidden group"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    

                    <img
                        alt={name}
                        src={Array.isArray(images) ? images[currentImageIndex] : images}
                        className="h-full w-full object-cover transition-all duration-300 ease-in-out"
                        onError={onImageError || defaultImageErrorHandler}
                    />

                    {/* Arrows */}
                    {hasMultipleImages && (
                        <>
                            <button
                                onClick={goPrev}
                                className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full z-10 opacity-0 group-hover:opacity-100 transition"
                            >
                                ‹
                            </button>

                            <button
                                onClick={goNext}
                                className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full z-10 opacity-0 group-hover:opacity-100 transition"
                            >
                                ›
                            </button>
                        </>
                    )}

                    {/* Dots */}
                    {hasMultipleImages && (
                        <div className="absolute bottom-2 left-0 right-0 justify-center flex gap-2 z-20">
                            {images.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-3 h-3 rounded-full transition-all border border-white/70 ${
                                        index === currentImageIndex
                                            ? 'bg-white scale-110'
                                            : 'bg-white/50'
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="h-56 w-full bg-sage/20 flex items-center justify-center">
                    <span className="text-charcoal/50">Ingen bild tillgänglig</span>
                </div>
            )}

            <div 
                className={`p-4 flex flex-col ${expandable ? 'cursor-pointer' : ''}`}
                onClick={expandable ? toggleExpanded : undefined}
            >
                <h3 className="font-heading text-lg font-semibold text-charcoal">
                    {name}
                </h3>

                {/* Area Tags */}
                {areas.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 mb-3">
                        {displayedAreas.map((area, index) => (
                            <span
                                key={index}
                                className="inline-block px-3 py-1 bg-sage text-charcoal text-xs font-medium rounded-full"
                            >
                                {area}
                            </span>
                        ))}
                        {remainingAreasCount > 0 && (
                            <span className="inline-block px-3 py-1 bg-sage text-charcoal text-xs font-medium rounded-full">
                                +{remainingAreasCount}
                            </span>
                        )}
                    </div>
                )}

                {/* Collapsible Content */}
                {expandable ? (
                    <>
                        <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                isExpanded ? 'max-h-96 opacity-100 mb-3' : 'max-h-0 opacity-0 mb-0'
                            }`}
                        >
                            {description && (
                                <p className="text-sm text-charcoal/70 leading-relaxed mb-2">
                                    {description}
                                </p>
                            )}

                            {habitat && (
                                <div className="mt-2 mb-2">
                                    <p className="text-xs font-semibold text-charcoal/80 uppercase tracking-wide">
                                        Habitat
                                    </p>
                                    <p className="text-sm text-charcoal/70 leading-relaxed">
                                        {habitat}
                                    </p>
                                </div>
                            )}

                            {soundPath && (
                                <div className="mt-3">
                                    <audio 
                                        controls 
                                        className="w-full h-8"
                                        style={{ maxHeight: '32px' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <source 
                                            src={`${process.env.REACT_APP_API_URL}/storage/uploads/species/${speciesId}/sounds/${soundPath}`} 
                                            type="audio/mpeg" 
                                        />
                                        Din webbläsare stöder inte ljuduppspelning.
                                    </audio>
                                </div>
                            )}
                        </div>

                        <button
                            className="pt-3 pb-1 flex justify-center text-forest hover:text-leaf transition-colors focus:outline-none hover:bg-sage/20 rounded-b-xl -mx-4 px-4 -mb-4"
                            aria-label={isExpanded ? "Dölj information" : "Visa information"}
                        >
                            <svg
                                className={`w-6 h-6 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </>
                ) : (
                    <div className="mb-3">
                        {description && (
                            <p className="text-sm text-charcoal/70 leading-relaxed mb-2">
                                {description}
                            </p>
                        )}

                        {habitat && (
                            <div className="mt-2 mb-2">
                                <p className="text-xs font-semibold text-charcoal/80 uppercase tracking-wide">
                                    Habitat
                                </p>
                                <p className="text-sm text-charcoal/70 leading-relaxed">
                                    {habitat}
                                </p>
                            </div>
                        )}

                        {soundPath && (
                            <div className="mt-3">
                                <audio 
                                    controls 
                                    className="w-full h-8"
                                    style={{ maxHeight: '32px' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <source 
                                        src={`${process.env.REACT_APP_API_URL}/storage/sounds/${soundPath}`} 
                                        type="audio/mpeg" 
                                    />
                                    Din webbläsare stöder inte ljuduppspelning.
                                </audio>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </article>
    );
}
